import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert,
  AlertTitle,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Fab,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  NotificationsActive,
  ClearAll,
  Close as CloseIcon,
  RocketLaunch,
  ReportProblem as ReportProblemIcon,
  Gavel,
  Shield,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { dashboardAPI, statusAPI, mt5API, metricsAPI } from '../services/api';
import AccountStatusTable from '../components/AccountStatusTable';
import DeployTab from '../components/DeployTab';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';

const ALERT_COLORS = {
  error: 'error',
  warning: 'warning',
  info: 'info',
  success: 'success',
};

const SYNC_STATUS_LABELS = {
  SYNC_OK: 'Đồng bộ',
  SYNC_MASTER_MORE: 'Master nhiều hơn',
  SYNC_SLAVE_MORE: 'Slave nhiều hơn',
  SYNC_MISMATCH: 'Không khớp',
};

// EA messages use \n for line breaks; convert to <br> for HTML rendering
const formatAlertMessage = (message) => {
  if (!message) return '';
  return message.replace(/\n/g, '<br>');
};

// Module-level: persist across tab switches (component unmount/remount)
// null = no filter on first fetch; set to ISO string after first load
let _lastAlertTime = null;
// Set of alert IDs already shown — prevents duplicates from REST + WS
const _seenAlertIds = new Set();

export default function Dashboard() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [syncAlerts, setSyncAlerts] = useState([]);
  const [snackbarAlert, setSnackbarAlert] = useState(null);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [focusedAccountId, setFocusedAccountId] = useState(null);

  const lastAlertTimeRef = useRef(_lastAlertTime);
  const seenAlertIds = useRef(_seenAlertIds);

  const { lastMessage } = useWebSocket();

  const { isLoading } = useQuery(
    'dashboard-stats',
    () => dashboardAPI.getStats().then(res => res.data),
    { refetchInterval: 10000 }
  );

  const { data: rotationData } = useQuery(
    'rotation-alerts',
    () => dashboardAPI.getRotationAlerts().then(res => res.data),
    { refetchInterval: 60000 } // check once a minute — streak is computed from daily buckets
  );
  const rotationAlerts = rotationData?.alerts || [];

  // ── V4.5 capital mgmt: buy/sell hôm nay từ AccountDailyStats ──────────────
  const { data: dailyStatsData, refetch: refetchDailyStats } = useQuery(
    'daily-stats',
    () => metricsAPI.getDailyStats().then(res => res.data),
    { refetchInterval: 30000 }
  );

  // "Hôm nay" = stat_date của EA (giờ server MT5 ≈ UTC, cùng quy ước utcDateStr của báo cáo 23h).
  // Cuối tuần TimeCurrent() của MT5 đứng ở tick cuối (thứ 6) nên dòng mới nhất vẫn là thứ 6
  // → phải so stat_date với hôm nay, không lấy bừa dòng mới nhất (bug 2026-08-23: CN vẫn hiện 11▲/20▼).
  const todayStatDate = (() => {
    const d = new Date();
    return `${d.getUTCFullYear()}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.${String(d.getUTCDate()).padStart(2, '0')}`;
  })();
  // Phiên giao dịch trong ngày kết thúc khi Mỹ đóng cửa; thị trường nghỉ đến khi Úc mở.
  // Reset thẻ lúc 22:00 UTC = 05:00 VN (luôn TRƯỚC 6h VN theo yêu cầu 2026-08-24):
  // từ 05:00 → 07:00 VN (EA sang ngày server mới lúc 00:00 UTC) thẻ hiện 0 + "Phiên gần nhất".
  const SESSION_END_UTC_HOUR = 22;
  const sessionOver = new Date().getUTCHours() >= SESSION_END_UTC_HOUR;
  const fmtStatDate = (s) => (s && s.length === 10 ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : s || '');


  // ── V4.5: cảnh báo kỷ luật (source=discipline) → banner + nhấp nháy bảng ──
  const [disciplineAlert, setDisciplineAlert] = useState(null);
  const disciplineTimerRef = useRef(null);
  const dismissDiscipline = useCallback(() => {
    if (disciplineTimerRef.current) clearTimeout(disciplineTimerRef.current);
    disciplineTimerRef.current = null;
    setDisciplineAlert(null);
  }, []);

  const [pollInterval, setPollInterval] = useState(5000);

  const { data: accountsData, refetch: refetchAccounts } = useQuery(
    'account-status',
    () => statusAPI.getAccounts().then(res => res.data),
    { refetchInterval: false }
  );

  // Manual polling — restarts cleanly whenever pollInterval changes
  useEffect(() => {
    const id = setInterval(() => { refetchAccounts(); }, pollInterval);
    return () => clearInterval(id);
  }, [pollInterval, refetchAccounts]);

  // Mỗi MASTER 1 hàng thẻ (2026-08-24): gộp số liệu riêng từng master.
  // - AccountDailyStats (RiskStats của Master EA): buy/sell, vi phạm, giới hạn lỗ ngày, khóa.
  //   Chỉ tính "hôm nay" khi stat_date == hôm nay (server/UTC) và phiên chưa đóng (trước 05:00 VN).
  // - AccountStatus (Status Reporter): open_positions = floating, closed_profit = đã chốt
  //   HÔM NAY (GMT+7), total_profit = floating + chốt.
  // Chưa có master nào (đang tải / chưa cấu hình) → 1 hàng placeholder giữ layout.
  const masterRows = useMemo(() => {
    const masters = (accountsData?.accounts || []).filter(a => Number(a.is_master));
    const rows = dailyStatsData?.data || [];
    const latestByAccount = {};
    for (const r of rows) {
      const cur = latestByAccount[r.mt5_account_id];
      if (!cur || r.stat_date > cur.stat_date) latestByAccount[r.mt5_account_id] = r;
    }
    const build = (m) => {
      const d = m ? latestByAccount[m.mt5_account_id] : null;
      const isToday = !sessionOver && !!d && d.stat_date === todayStatDate;
      const lastBuy = d?.buy_count || 0;
      const lastSell = d?.sell_count || 0;
      const lastViolations = d?.violations || 0;
      return {
        key: m ? m.mt5_account_id : 'no-master',
        label: m ? `${m.name || m.mt5_account_id} (${m.mt5_account_id})` : 'Master (chưa có dữ liệu)',
        isToday,
        lastDate: d?.stat_date || null,
        buy: isToday ? lastBuy : 0,
        sell: isToday ? lastSell : 0,
        lastBuy, lastSell,
        violations: isToday ? lastViolations : 0,
        lastViolations,
        maxLoss: d && Number(d.daily_max_loss) > 0 ? Number(d.daily_max_loss) : null,
        locked: d ? Number(d.locked) === 1 : false,
        floating: Number(m?.open_positions) || 0,
        closed: Number(m?.closed_profit) || 0,
        total: Number(m?.total_profit) || 0,
      };
    };
    return masters.length > 0 ? masters.map(build) : [build(null)];
  }, [accountsData, dailyStatsData, todayStatDate, sessionOver]);

  // Go to 1.5s on any error; revert to 5s when all clear
  useEffect(() => {
    const accounts = accountsData?.accounts || [];
    const hasAnyError = accounts.some(a => Number(a.has_error) > 0);
    setPollInterval(hasAnyError ? 1500 : 5000);
  }, [accountsData]);

  // Called by table buttons — jump to fast polling immediately
  const handleFastPoll = useCallback(() => {
    setPollInterval(1500);
    refetchAccounts();
  }, [refetchAccounts]);

  const { data: syncAlertsData } = useQuery(
    'sync-alerts',
    () => mt5API.getSyncAlerts(lastAlertTimeRef.current).then(res => res.data),
    { refetchInterval: 3000 }
  );


  // Set of account IDs that currently have active error-type sync alerts
  const errorAlertIds = useMemo(
    () => new Set(syncAlerts.filter(a => a.alert_type === 'error').map(a => String(a.slave_account_id))),
    [syncAlerts]
  );

  const playAlertSound = useCallback((alertType) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = alertType === 'error' ? 550 : 800;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.6);
    } catch (e) { /* ignore */ }
  }, []);

  // ── Helper: add alerts deduplicating by ID ─────────────────────────────────
  const pushAlerts = useCallback((incoming, { silent = false } = {}) => {
    const fresh = incoming.filter(a => !seenAlertIds.current.has(a.id));
    if (fresh.length === 0) return;
    fresh.forEach(a => seenAlertIds.current.add(a.id));
    setSyncAlerts(prev => [...fresh, ...prev].slice(0, 20));

    if (silent) return;

    const latest = fresh[0];
    setSnackbarAlert(latest);
    if (latest.alert_type === 'error' || latest.alert_type === 'warning') {
      playAlertSound(latest.alert_type);
    }
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`⚠️ MT5 Alert [${latest.alert_type?.toUpperCase()}]`, {
        body: `${latest.slave_account_id}: ${latest.message?.replace(/(<[^>]+>|\n)/g, ' ')}`,
        tag: `sync-alert-${latest.id}`,
        icon: '/favicon.ico',
        requireInteraction: latest.alert_type === 'error',
      });
    }
  }, [playAlertSound]);

  // ── V4.5: live risk stats update qua WebSocket ─────────────────────────────
  useEffect(() => {
    if (!lastMessage || lastMessage.type !== 'mt5_risk_stats') return;
    refetchDailyStats();
  }, [lastMessage, refetchDailyStats]);

  // ── V4.5: alert kỷ luật → banner + nhấp nháy toàn bộ dòng (tự tắt sau 5 phút)
  useEffect(() => {
    if (!lastMessage || lastMessage.type !== 'mt5_alert') return;
    if (lastMessage.source !== 'discipline' && lastMessage.source !== 'risk') return;
    setDisciplineAlert({
      message: lastMessage.message || '',
      name: lastMessage.slaveName || lastMessage.slave_account_id || '',
      severity: lastMessage.alert_type === 'error' ? 'error' : 'warning',
      at: Date.now(),
    });
    if (disciplineTimerRef.current) clearTimeout(disciplineTimerRef.current);
    disciplineTimerRef.current = setTimeout(() => setDisciplineAlert(null), 5 * 60 * 1000);
  }, [lastMessage]);

  useEffect(() => () => {
    if (disciplineTimerRef.current) clearTimeout(disciplineTimerRef.current);
  }, []);

  // ── Real-time: WebSocket push from console-app bridge ──────────────────────
  useEffect(() => {
    if (!lastMessage || lastMessage.type !== 'mt5_alert') return;
    pushAlerts([lastMessage]);
    // Keep REST cursor in sync so the next poll doesn't re-fetch this alert
    const ts = new Date(lastMessage.received_at).toISOString();
    if (!lastAlertTimeRef.current || ts > lastAlertTimeRef.current) {
      lastAlertTimeRef.current = ts;
      _lastAlertTime = ts;
    }
  }, [lastMessage, pushAlerts]);

  // ── Fallback: REST polling (initial load + connection-loss recovery) ────────
  useEffect(() => {
    if (!syncAlertsData?.alerts) return;

    const isInitialFetch = lastAlertTimeRef.current === null;
    const newAlerts = syncAlertsData.alerts;

    if (newAlerts.length > 0) {
      const ts = new Date(newAlerts[0].received_at).toISOString();
      lastAlertTimeRef.current = ts;
      _lastAlertTime = ts;
      // Silent on first load; audible only for truly new alerts arriving via polling
      pushAlerts(newAlerts, { silent: isInitialFetch });
    } else if (isInitialFetch) {
      const ts = new Date().toISOString();
      lastAlertTimeRef.current = ts;
      _lastAlertTime = ts;
    }
  }, [syncAlertsData, pushAlerts]);

  // Update browser tab title with notification count; clear on focus
  useEffect(() => {
    document.title = syncAlerts.length > 0
      ? `(${Math.min(syncAlerts.length, 99)}) MT5 Dashboard`
      : 'MT5 Dashboard';
  }, [syncAlerts.length]);

  useEffect(() => {
    const handleFocus = () => { document.title = 'MT5 Dashboard'; };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const CARD_GRADIENTS = {
    primary: 'linear-gradient(135deg, #0d4f6e 0%, #083346 100%)',
    success: 'linear-gradient(135deg, #0d5c3a 0%, #073d27 100%)',
    warning: 'linear-gradient(135deg, #3d1f7a 0%, #27124f 100%)',
    error: 'linear-gradient(135deg, #7a1f1f 0%, #4f1212 100%)',
  };

  const fmtUsd = (v) => { const n = Number(v) || 0; return `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`; };

  // `sub` (tùy chọn): nội dung phụ dưới giá trị chính (dòng chi tiết / thanh tiến trình)
  const StatCard = ({ title, value, icon, color = 'primary', sub = null, tooltip = '' }) => (
    <Card
      sx={{
        background: CARD_GRADIENTS[color] || CARD_GRADIENTS.primary,
        color: 'white',
        border: '1px solid rgba(255,255,255,0.07)',
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={tooltip} disableHoverListener={!tooltip}>
              <Typography
                sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', mb: 1, fontWeight: 500, cursor: tooltip ? 'help' : 'default' }}
              >
                {title}
              </Typography>
            </Tooltip>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
              {value}
            </Typography>
            {sub && <Box sx={{ mt: 1 }}>{sub}</Box>}
          </Box>
          <Box
            sx={{
              backgroundColor: 'rgba(255,255,255,0.12)',
              borderRadius: '12px',
              width: 52,
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const errorCount = syncAlerts.filter(a => a.alert_type === 'error').length;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        Dashboard
      </Typography>

      {/* Tab bar — Deploy tab only shown to admins */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Tổng quan" />
          {isAdmin() && (
            <Tab label="Deploy EA" icon={<RocketLaunch fontSize="small" />} iconPosition="start" />
          )}
        </Tabs>
      </Box>

      {/* Tab 0: Overview */}
      {activeTab === 0 && (
        <>
      {/* Mỗi MASTER 1 hàng 4 thẻ (2026-08-24): Lệnh hôm nay · Vi phạm · Lỗ/Giới hạn ngày · Tổng lãi/lỗ */}
      {masterRows.map((mr) => (
        <Box key={mr.key} sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            👑 {mr.label}{mr.locked ? '  🔒' : ''}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Lệnh hôm nay (Buy / Sell)"
                value={`${mr.buy} ▲ / ${mr.sell} ▼`}
                icon={mr.buy >= mr.sell ? <TrendingUp /> : <TrendingDown />}
                color={!mr.isToday ? 'primary' : mr.buy >= mr.sell ? 'success' : 'error'}
                tooltip={mr.isToday
                  ? `Số lệnh ${mr.label} MỞ trong ngày (giờ server MT5) — từ RiskStats của Master EA`
                  : `Phiên hôm nay đã đóng cửa (reset 05:00 VN sau phiên Mỹ) hoặc chưa mở. Số liệu phiên gần nhất ${fmtStatDate(mr.lastDate)}: ${mr.lastBuy}▲ / ${mr.lastSell}▼`}
                sub={!mr.isToday && mr.lastDate ? (
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                    Phiên gần nhất {fmtStatDate(mr.lastDate)}: {mr.lastBuy}▲ / {mr.lastSell}▼
                  </Typography>
                ) : null}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Vi phạm hôm nay"
                value={mr.violations}
                icon={<Gavel />}
                color={mr.violations > 0 ? 'error' : 'success'}
                tooltip={`Số lần ${mr.label} vi phạm nguyên tắc trong ngày (không SL lỗ sâu / đóng lệnh lỗ quá ngưỡng / sai volume) — chi tiết ở tab Hiệu suất master`}
                sub={(
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                    {!mr.isToday && mr.lastDate
                      ? `Hôm nay chưa có phiên · phiên ${fmtStatDate(mr.lastDate)}: ${mr.lastViolations} vi phạm`
                      : mr.violations > 0 ? '⚠ Xem lịch kỷ luật ở Hiệu suất master' : '✓ Chưa có vi phạm'}
                  </Typography>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {(() => {
                const closed = mr.closed;
                const limit = mr.maxLoss;                // null = EA chưa xuất / tắt
                const lossAbs = closed < 0 ? -closed : 0;
                const pct = limit ? Math.min(100, (lossAbs / limit) * 100) : 0;
                const barColor = pct >= 100 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#22c55e';
                const remain = limit ? Math.max(0, limit - lossAbs) : null;
                return (
                  <StatCard
                    title={`Lỗ hôm nay / Giới hạn ngày${mr.locked ? ' 🔒' : ''}`}
                    value={fmtUsd(closed)}
                    icon={<Shield />}
                    color={closed < 0 ? (pct >= 70 ? 'error' : 'warning') : 'success'}
                    tooltip={`Lãi/lỗ ĐÃ CHỐT hôm nay của ${mr.label} so với giới hạn lỗ ngày InpDailyMaxLoss. Thanh = % giới hạn đã dùng; chạm 100% Master tự đóng hết & khóa đến 22h server.`}
                    sub={(
                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.35)',
                                '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 3 } }}
                        />
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem', mt: 0.5 }}>
                          Giới hạn: {limit ? `$${limit.toFixed(2)}` : '—'}
                          {limit ? ` · còn ${fmtUsd(remain)}` : ''}
                          {mr.locked ? ' · ĐANG KHÓA' : ''}
                        </Typography>
                      </Box>
                    )}
                  />
                );
              })()}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Tổng lãi / lỗ"
                value={fmtUsd(mr.total)}
                icon={<AccountBalanceWallet />}
                color={mr.total >= 0 ? 'primary' : 'error'}
                tooltip={`${mr.label}: Tổng = Vị thế (floating đang mở) + Đã chốt hôm nay`}
                sub={(
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                    Vị thế: <b style={{ color: mr.floating >= 0 ? '#86efac' : '#fca5a5' }}>{fmtUsd(mr.floating)}</b>
                    {'   '}Đã chốt: <b style={{ color: mr.closed >= 0 ? '#86efac' : '#fca5a5' }}>{fmtUsd(mr.closed)}</b>
                  </Typography>
                )}
              />
            </Grid>
          </Grid>
        </Box>
      ))}

      {/* V4.5: Banner cảnh báo kỷ luật — kèm nhấp nháy toàn bộ dòng bảng */}
      {disciplineAlert && (
        <Alert
          severity={disciplineAlert.severity}
          sx={{
            mb: 3,
            border: '2px solid #ef444480',
            animation: 'disciplinePulse 1s ease-in-out infinite',
            '@keyframes disciplinePulse': {
              '0%, 100%': { boxShadow: '0 0 0 0 rgba(239,68,68,0.6)' },
              '50%': { boxShadow: '0 0 14px 4px rgba(239,68,68,0.35)' },
            },
          }}
          action={
            <IconButton size="small" color="inherit" onClick={dismissDiscipline}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          <AlertTitle sx={{ fontWeight: 700 }}>
            ⚠️ CẢNH BÁO KỶ LUẬT — {disciplineAlert.name}
          </AlertTitle>
          <span dangerouslySetInnerHTML={{ __html: formatAlertMessage(disciplineAlert.message) }} />
        </Alert>
      )}

      {rotationAlerts.length > 0 && (
        <Alert
          severity="warning"
          icon={<ReportProblemIcon />}
          sx={{ mb: 3, border: '1px solid #f59e0b60', alignItems: 'flex-start' }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>
            Cảnh báo đổi tài khoản ({rotationAlerts.length})
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            Các tài khoản sau đã giao dịch ≥ 30 ngày liên tục — nên đổi sang tài khoản mới để tránh bị broker bóp margin/spread.
          </Typography>
          <Stack spacing={0.75}>
            {rotationAlerts.map(a => (
              <Box key={a.mt5_account_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  label={`${a.streak_days} ngày`}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 700, minWidth: 72 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {a.mt5_account_id} — {a.account_name || '(chưa đặt tên)'}
                </Typography>
                {a.customer_name && (
                  <Chip
                    label={`KH: ${a.customer_name}`}
                    size="small"
                    variant="outlined"
                  />
                )}
                {a.group_name && (
                  <Chip
                    label={a.group_name}
                    size="small"
                    variant="outlined"
                    sx={{ color: 'text.secondary' }}
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  streak từ {a.streak_start}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Alert>
      )}

      <Paper sx={{ p: { xs: 1, sm: 2, md: 3 }, width: '100%', overflowX: 'hidden' }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Trạng thái tài khoản
        </Typography>
        <AccountStatusTable
          accounts={accountsData?.accounts || []}
          master={accountsData?.master}
          errorAlertIds={errorAlertIds}
          focusedAccountId={focusedAccountId}
          onFastPoll={handleFastPoll}
          blinkAll={!!disciplineAlert}
        />
      </Paper>
        </>
      )}

      {/* Tab 1: Deploy EA (admin only) */}
      {activeTab === 1 && isAdmin() && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <DeployTab />
        </Paper>
      )}

      {/* Floating alert button */}
      <Tooltip title={syncAlerts.length > 0 ? `${syncAlerts.length} thông báo` : 'Không có thông báo'}>
        <Fab
          color={errorCount > 0 ? 'error' : 'warning'}
          size="medium"
          onClick={() => setAlertDialogOpen(true)}
          sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1200 }}
        >
          <Badge badgeContent={Math.min(syncAlerts.length, 99)} color="default" max={99}>
            <NotificationsActive />
          </Badge>
        </Fab>
      </Tooltip>

      {/* Alert dialog */}
      <Dialog
        open={alertDialogOpen}
        onClose={() => setAlertDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsActive color={errorCount > 0 ? 'error' : 'warning'} />
            <Typography fontWeight={700}>Cảnh báo đồng bộ MT5</Typography>
            {errorCount > 0 && <Chip label={`${errorCount} lỗi`} color="error" size="small" />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Xóa tất cả">
              <IconButton size="small" onClick={() => { setSyncAlerts([]); setAlertDialogOpen(false); }}>
                <ClearAll fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setAlertDialogOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          <Stack spacing={0} divider={<Divider />}>
            {syncAlerts.map(alert => (
              <Alert
                key={alert.id}
                severity={ALERT_COLORS[alert.alert_type] || 'info'}
                sx={{
                  borderRadius: 0,
                  py: 1,
                  cursor: 'pointer',
                  '&:hover': { filter: 'brightness(0.95)' },
                }}
                onClick={() => {
                  const id = String(alert.slave_account_id);
                  setFocusedAccountId(id);
                  setAlertDialogOpen(false);
                  setTimeout(() => setFocusedAccountId(null), 50);
                }}
              >
                <AlertTitle
                  sx={{
                    mb: 0.5,
                    fontSize: '0.85rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <strong>{alert.slave_account_id}</strong>
                  <span>—</span>
                  <span>{SYNC_STATUS_LABELS[alert.sync_status] || alert.sync_status}</span>
                  {alert.error_type && (
                    <Chip
                      label={alert.error_type}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.7rem' }}
                    />
                  )}
                  <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {new Date(alert.received_at).toLocaleTimeString('vi-VN')}
                  </Typography>
                </AlertTitle>
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.8rem', wordBreak: 'break-word' }}
                  component="div"
                  dangerouslySetInnerHTML={{ __html: formatAlertMessage(alert.message) }}
                />
                <Typography variant="caption" color="text.secondary">
                  Master: {alert.master_count} vị thế / {alert.master_pending} pending
                  {' · '}
                  Slave: {alert.slave_count} vị thế / {alert.slave_pending} pending
                </Typography>
              </Alert>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Snackbar toast for new alerts arriving while page is open */}
      <Snackbar
        open={!!snackbarAlert}
        autoHideDuration={snackbarAlert?.alert_type === 'error' ? 8000 : 5000}
        onClose={() => setSnackbarAlert(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snackbarAlert ? (
          <Alert
            onClose={() => setSnackbarAlert(null)}
            severity={ALERT_COLORS[snackbarAlert.alert_type] || 'info'}
            variant="filled"
            sx={{ minWidth: 280 }}
          >
            <AlertTitle>{snackbarAlert.slave_account_id}</AlertTitle>
            <span dangerouslySetInnerHTML={{ __html: snackbarAlert.message }} />
          </Alert>
        ) : <div />}
      </Snackbar>
    </Box>
  );
}
